import React, { useState } from 'react';
import { Box, Text, Heading, Center, Button, useToast, Image } from '@chakra-ui/react';
import { string } from 'prop-types';
import copyToClipboard from '@invitato/helpers/dist/copyTextToClipboard';

import useLang from '@/hooks/useLang';
import WithAnimation from '@/components/Common/WithAnimation';

import { BUTTON_PADDING_PROPS, BG_LAYER } from '@/constants/colors';
import { ENABLE_GIFT_CARD } from '@/constants/feature-flags';
import { ASSETS_BUTTERFLY } from '@/constants/assets';
import { TEXT_PROPS } from '@/constants/default-props';

import txtWording from './locales';
import ModalGiftCard from './ModalGiftCard';

const MODAL_STATE = {
  bank: 'bank',
  address: 'address',
  idle: '',
  open: 'open',
  confirm: 'confirmation',
};

/**
 * Function to render GiftCard component with Modal Info
 * @returns {JSX.Element}
 */
function GiftCardSection({ ...rest }) {
  const [modalState, setModalState] = useState(MODAL_STATE.idle);
  const toast = useToast();
  const lang = useLang();

  const onCopyText = async (text, message = '') => {
    const result = await copyToClipboard(text);

    if (result) {
      setModalState(MODAL_STATE.idle);
      toast({
        title: txtWording.success[lang],
        description: message || txtWording.successCopyRekening[lang],
        status: 'success',
        duration: 9000,
        isClosable: true,
      });
    } else {
      toast({
        title: 'Oops!',
        description: txtWording.failedCopied[lang],
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  };

  const onClose = () => {
    setModalState(MODAL_STATE.idle);
  };

  return (
    <Box px={{ '3sm': '60px', '2sm': '16px', sm: '16px' }}>
      <Box {...rest} marginTop="100px" pos="relative">
        <Center
          width="100%"
          mx="auto"
          pos="relative"
          borderRadius="16px"
          border="1px solid"
          borderColor="bgSecondary"
          bgColor={BG_LAYER}
          minHeight="40vh"
          padding="24px"
          my="24px"
          {...TEXT_PROPS.body}
          textAlign="center"
        >
          {/* ASSETS BUTTERFLY */}
          <Box
            className="animation__butterfly"
            position="absolute"
            top={{ '3sm': '30px', '2sm': '40px', '1sm': '30px', sm: '0px' }}
            left={{ '3sm': '30px', '2sm': '20px', '1sm': '15px', sm: '0px' }}
            zIndex="3"
          >
            <WithAnimation left delay={500}>
              <Image src={ASSETS_BUTTERFLY} maxW="50px" />
            </WithAnimation>
          </Box>

          <Center flexDir="column">
            <WithAnimation>
              <Heading {...TEXT_PROPS.heading} px="8px">
                {txtWording.title[lang]}
              </Heading>
            </WithAnimation>
            <WithAnimation>
              <Text {...TEXT_PROPS.body} align="center" padding="0 8px" my="16px">
                {txtWording.desc[lang]}
              </Text>
            </WithAnimation>
            {ENABLE_GIFT_CARD && (
              <>
                <WithAnimation>
                  <Button {...BUTTON_PADDING_PROPS} onClick={() => setModalState(MODAL_STATE.open)}>
                    {txtWording.sendGift[lang]}
                  </Button>
                </WithAnimation>
              </>
            )}
          </Center>
        </Center>

        <ModalGiftCard
          lang={lang}
          onClose={onClose}
          visible={modalState === MODAL_STATE.open}
          onCopyText={onCopyText}
        />
      </Box>
    </Box>
  );
}

GiftCardSection.propTypes = {
  lang: string,
};

GiftCardSection.defaultProps = {
  lang: 'en',
};

export default React.memo(GiftCardSection);
